// eslint:disable no-use-before-declare
import { ApplicationState } from 'app/types-store';
import { CaseState } from 'app/area/case/types-store';
import { invariant } from 'app/infrastructure/log';
import { ControlType, IteratorType } from 'app/infrastructure/ui/component-configuration';
import { 
  InitialUiEngineProps, 
  DefaultValueDefinition, 
  UiDefaultValueDefinition, 
  UiSchema, 
  ComponentDescriptor,
  ConnectedComponentProps,
  ComponentProps,
  ComponentCallbacks
} from 'app/infrastructure/ui/types-uischema';
import { UiDefinition as UiConfiguration, Domain } from 'app/infrastructure/ui/types-uidefinition';
import { RenderMode } from 'app/types-shared';
import { clonePrototype } from 'clone';
import {
  resolveComponent,
  resolveComponentDefaultProps,
  resolveComponentEvents,
  resolveComponentEnhancers,
  resolveComponentSelectors,
  resolveComponentIteration
} from 'app/infrastructure/ui/component-registry';
import { PropertyBuilder } from './property-builder';
import { resolveComponentDefaultValues } from 'app/infrastructure/ui/builder/default-values';
import { memoize } from 'app/infrastructure/js';

/**
 * Repeated calls to build a schema returns the same instance given the 
 * equal object references for config, and value reference for mode
 * for the same combination of applicationId, domainContext and rootDomain
 */
const schemaCache = new Map();
const memoized = (mode: RenderMode, config: UiConfiguration) => {
  const key = JSON.stringify([mode, config.caseTypeId, config.domainContext]);
  if (!schemaCache.has(key)) {
    const memoizedFunction = memoize((innerMode: RenderMode, innerConfig: UiConfiguration) => new SchemaBuilder()
      .usingModeResolver(() => innerMode)
      .fromConfiguration(innerConfig)
      .build()
    );
    schemaCache.set(key, memoizedFunction);
  }
  return schemaCache.get(key)(mode, config);
};

/**
 * SchemaBuilder builds a typed render schema from a JSON configuration
 * 
 * @export
 * @class SchemaBuilder
 */
export class SchemaBuilder implements UiSchema {
    public contextId: number;
    public applicationId: number;
    public id: string;
    public children: ComponentDescriptor[];
    private config: UiConfiguration;
    private modeResolver: (controlType: number, componentId: number) => RenderMode;

    // eslint:disable-next-line:function-name
    public static create(mode: RenderMode, config: UiConfiguration) : UiSchema {
        if (!config) {
          return undefined;
        }
        return memoized(mode, config); 
    }

    /**
     * Use this method to assign the configuration that the UI schema 
     * should be built from
     * 
     * @param {UiConfiguration} config
     * @returns {Schema}
     * 
     * @memberOf Schema
     */
    public fromConfiguration(config: UiConfiguration): SchemaBuilder {
        this.config = config;
        this.throwIfConfigIsInvalid();
        return this;
    }

    /**
     * The resolver allows every single component to ask for it's render mode.
     * By providing it's id we are flexible in terms of overriding individual component modes at runtime, e.g inline edit
     * Passed trough to the component descriptor builder
     * 
     * @param {(componentId: number) => string} resolverFunction
     * 
     * @memberOf Schema
     */
    public usingModeResolver(resolver: (controlType: number, componentId: number) => RenderMode) {
        this.modeResolver = resolver;
        return this;
    }

    /**
     * Builds the actual UiSchema and Component descriptors
     * 
     * @returns {UiSchema}
     * 
     * @memberOf Schema
     */
    public build(): UiSchema {
        invariant( typeof this.modeResolver === 'function', 'modeResolver is required');
        this.applicationId = this.config.caseTypeId;
        this.contextId = this.config.domainContext;
        this.children = [] as ComponentDescriptor[];
        this.mapComponents();
        return this;
    }

    /**
     * When serializing it's not neccessary to include e.g the configuration
     * as a duplicate so it will be removed
     * 
     * @returns
     * 
     * @memberOf Schema
     */
    public toJSON() {
        const excluded = ['config'];
        const cloned: any = clonePrototype(this as UiSchema);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        excluded.forEach((p: any) => delete cloned[p]);
        return cloned;
    }

    private mapComponents() {
        const rootComponent = new ComponentSchema()
            .fromConfiguration(this.config.rootDomain)
            .build(this.modeResolver);
        this.children.push(rootComponent);
    }

    private throwIfConfigIsInvalid() {
        const s = this.config;
        const missingProps = !s.domainContext || !s.rootDomain || !s.rootDomain.children;
        if (missingProps) {
            throw new Error(`Required properties missing in UiConfiguration ${s.rootDomain}`);
        }
    }

    private sectionVisibleForCase = 
      (section: ComponentDescriptor & {config: {[key:string]:any}} ) => 
        section.properties.visible && 
          ( section.config.controlType === ControlType.SectionPanel || section.config.controlType === ControlType.ProcessPanel);

    public get availableSections() : ComponentDescriptor[] {
      return this.children[0].children.filter(this.sectionVisibleForCase);
    }

    /**
     * Returns the Schema config for the child with id matching `domainId`
     *
     * @param domainId 
     */    
    public getById(domainId: number) : ComponentDescriptor {    

      const findChild = (children:ComponentDescriptor[] = [])  =>{
        for(const child of children){
          if(child.id === domainId){
            return child;
          }
          const nested = findChild(child.children);
          if(nested){
            return nested;
          }
        }
      };

      for(const root of this.children){
        if(root.id === domainId){
          return root;
        }
        const child = findChild(root.children);
        if(child){
          return child;
        }
      }
      return undefined;
  }
}

/**
 * The typed representation of Components in the UiSchema format
 * 
 * @class ComponentSchema
 * @implements {ComponentDescriptor}
 */
export class ComponentSchema implements ComponentDescriptor {

    /**
     * The persisted primary key. aka "Domain id"
     */
    public id: number;
    /**
     * Translated label text. Resolved server side.
     */
    public label: string;
    /**
     * Name of connected entities property whose value is represented by this component
     */
    public dataTag: string;
    /**
     * Path of ancestor entities for this component. Left to right, highest to lowest level.
     */
    public entityTypePath: string[];
    /**
     * The react component that renders the visual representation
     */
    public componentClass: any;
    /**
     * List of higher order components that the componentClass will be wrapped in.
     * Prefer selectors where the props are based on the state data.
     */
    public enhancers: ((componentClass: React.ComponentClass<any> | React.FC<any>, props?: InitialUiEngineProps) => 
      React.ComponentClass<any> | React.FC<any>)[];
    /**
     * Array of selectors providing extra props, usually selected from the provided state, to the components.
     */
    public selectors: (( state: CaseState, props: ConnectedComponentProps, fullState: ApplicationState) => any)[];
    /**
     * Friendly name of the React component. Resolved from name of [ControlType] enum.
     */
    public componentName: string;
    /**
     * Display/render order. Determined server side.
     */
    public sequence: number;
    /**
     * Child components. Not used for leaf nodes.
     */
    public children: ComponentDescriptor[];
    /**
     * Intepreted and typed mappings of server side "Domain Attributes"
     */
    public properties: ComponentProps;
    /**
     * Functions or Actions the React component rendering this can execute
     */
    public callbacks: ComponentCallbacks;
    /**
     * Client side events the component would like to have callbacks provided by the ui engine
     */
    public componentEvents: string[];
    /**
     * Default props passed into the representing React component, independent of which component performs the rendering
     */
    public componentDefaultProps: object;
    /**
     * Configuration for iterator components.
     */
    public iterate: { type: IteratorType; defaultValues: UiDefaultValueDefinition[] };
    /**
     * When this components value is a reference to a code. 
     * This is the id of the codetable where codes are loaded from.
     */
    public codetable: number;
    
    /**
     * definition of how to calculate or fetch the default value for component.
     */
    public defaultValue: DefaultValueDefinition;
      
    private config: Domain;

    /**
     * Use this method to provide the specific part of configuration
     * that this component should be built from.
     * 
     * @param {Domain} config
     * @returns
     * 
     * @memberOf ComponentSchema
     */
    public fromConfiguration(config: Domain) {
      this.config = config;
      return this;
    }

    /**
     * Builds the representation of the Component from configuration
     * 
     * @returns {ComponentDescriptor}
     * 
     * @memberOf ComponentSchema
     */
    public build(modeResolver: (controlType: number, componentId: number) => RenderMode): ComponentDescriptor {
        this.id = this.config.domainId;
        this.label = this.config.label;
        this.dataTag = this.config.dataTag;
        this.entityTypePath = this.config.entityTypePath;
        this.sequence = this.config.sequence;
        this.componentName = ControlType[this.config.controlType];
        const mode = modeResolver(this.config.controlType, this.id);
        this.componentClass = resolveComponent(this.config.controlType, mode);
        this.enhancers = resolveComponentEnhancers(this.config.controlType, mode);
        this.selectors = resolveComponentSelectors(this.config.controlType, mode);
        this.componentEvents = resolveComponentEvents(this.config.controlType, mode);
        this.componentDefaultProps = resolveComponentDefaultProps(this.config.controlType);
        this.codetable = this.config.codetable;
        this.defaultValue = this.config.defaultValue;
        
        invariant(!!this.componentClass, `componentClass did not fall back to a default component for ${this.config.controlType}:${mode}`);

        this.buildProperties(mode);

        if (this.config.children) {
            this.children =
                this.config.children.slice(0)
                .map(config => new ComponentSchema().fromConfiguration(config).build(modeResolver));
        }

        const iteratorConfig = resolveComponentIteration(this.config.controlType, mode);

        this.iterate = iteratorConfig && {
          ...iteratorConfig,
          defaultValues: resolveComponentDefaultValues(this)
        };

        return this;
    }

    private buildProperties(renderMode: RenderMode){
      
      const builder = new PropertyBuilder(this.config, renderMode);

      this.properties = builder.properties as any;

    }

    /**
     * When serializing it's not neccessary to include e.g the configuration
     * as a duplicate so it will be removed
     * 
     * @returns
     * 
     * @memberOf Schema
     */
    public toJSON() {
        const excluded = ['config'];
        const cloned: any = clonePrototype(this);
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        excluded.forEach((p: any) => delete cloned[p]);
        return cloned;
    }

}
